import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  ctaNavLink,
  primaryNavLinks,
  siteBrandName,
} from '../../content/navigation';

type MainNavProps = {
  transparentAtTop?: boolean;
};

export default function MainNav({ transparentAtTop = false }: MainNavProps) {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!transparentAtTop) {
      setIsScrolled(true);
      return;
    }
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [transparentAtTop]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-border/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="shrink-0 flex items-center gap-2.5 cursor-pointer">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <span className="text-white text-xs font-bold">C</span>
          </div>
          <span className="text-lg font-bold text-primary">{siteBrandName}</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-1">
          {primaryNavLinks.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  isActive
                    ? 'text-primary font-semibold'
                    : 'text-textMuted hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          <div className="w-px h-5 bg-border mx-3" />

          <Link
            to={ctaNavLink.to}
            className="px-5 py-2 text-sm font-semibold rounded-lg text-white bg-primary hover:bg-secondary transition-colors"
          >
            {ctaNavLink.label}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 text-primary"
          aria-label="メニュー"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-border px-6 py-4 space-y-1 animate-fade-in">
          {primaryNavLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block px-3 py-2.5 text-sm text-textMuted hover:text-primary rounded-md transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <div className="pt-3 mt-2 border-t border-border">
            <Link to={ctaNavLink.to} className="block px-3 py-2.5 text-sm font-semibold text-primary">
              {ctaNavLink.label}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
